'use client';

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, ArrowUp, ArrowDown, Lock } from 'lucide-react';
import { apiClient } from '@/lib/api-client';

/**
 * Shared claim-status management screen — reused by both the superadmin
 * per-tenant override page and the tenant-admin self-service settings page.
 * Superadmin-authored rows (scope='global') can only be edited by superadmin;
 * tenant-admin-authored rows (scope='tenant') belong to that tenant alone.
 */

interface JobType {
  id: number;
  code: string;
  name: string;
}

interface ClaimStatus {
  id: number;
  tenant_id: string;
  job_type_id: number;
  code: string;
  label: string;
  color: string;
  is_active: boolean;
  sort_order: number;
  scope: 'global' | 'tenant';
}

const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue', cls: 'bg-blue-100 text-blue-700' },
  { value: 'yellow', label: 'Yellow', cls: 'bg-yellow-100 text-yellow-700' },
  { value: 'green', label: 'Green', cls: 'bg-green-100 text-green-700' },
  { value: 'emerald', label: 'Emerald', cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'red', label: 'Red', cls: 'bg-red-100 text-red-700' },
  { value: 'orange', label: 'Orange', cls: 'bg-orange-100 text-orange-700' },
  { value: 'gray', label: 'Gray', cls: 'bg-gray-100 text-gray-700' },
];

function colorCls(color: string) {
  return COLOR_OPTIONS.find(c => c.value === color)?.cls || COLOR_OPTIONS[COLOR_OPTIONS.length - 1].cls;
}

function slugify(label: string) {
  return label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

interface Props {
  tenantId: string;
  /** true = tenant-admin context: no tenant/usecase picker beyond usecase, tenantId fixed */
  lockTenant?: boolean;
  /** true = caller is super_admin: creates scope='global' rows, can edit any row */
  isSuperAdmin: boolean;
}

export function ClaimStatusManager({ tenantId, lockTenant = false, isSuperAdmin }: Props) {
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);
  const [jobTypeId, setJobTypeId] = useState<number | null>(null);
  const [statuses, setStatuses] = useState<ClaimStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('gray');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.getTenantUseCases(tenantId)
      .then((useCases: any[]) => {
        const types = useCases.map(uc => ({ id: uc.job_type_id, code: uc.job_type_code, name: uc.job_type_name }));
        setJobTypes(types);
        if (types.length > 0) setJobTypeId(types[0].id);
      })
      .catch((err: any) => setError(err.response?.data?.detail || 'Failed to load usecases'));
  }, [tenantId]);

  const loadStatuses = useCallback(() => {
    if (!jobTypeId) return;
    setLoading(true);
    apiClient.listClaimStatusesAdmin(tenantId, jobTypeId, true)
      .then((data: ClaimStatus[]) => setStatuses(data))
      .catch((err: any) => setError(err.response?.data?.detail || 'Failed to load claim statuses'))
      .finally(() => setLoading(false));
  }, [tenantId, jobTypeId]);

  useEffect(() => { loadStatuses(); }, [loadStatuses]);

  async function handleCreate() {
    if (!jobTypeId || !newLabel.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await apiClient.createClaimStatus(tenantId, {
        job_type_id: jobTypeId,
        code: slugify(newLabel),
        label: newLabel.trim(),
        color: newColor,
        sort_order: statuses.length,
      });
      setNewLabel('');
      setNewColor('gray');
      setDialogOpen(false);
      setSuccess('Status added successfully!');
      setTimeout(() => setSuccess(null), 3000);
      loadStatuses();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create status');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(s: ClaimStatus) {
    try {
      setError(null);
      await apiClient.updateClaimStatus(tenantId, s.id, { is_active: !s.is_active });
      loadStatuses();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update status');
    }
  }

  async function handleReorder(s: ClaimStatus, direction: -1 | 1) {
    const sorted = [...statuses].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex(x => x.id === s.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    try {
      setError(null);
      await Promise.all([
        apiClient.updateClaimStatus(tenantId, s.id, { sort_order: other.sort_order }),
        apiClient.updateClaimStatus(tenantId, other.id, { sort_order: s.sort_order }),
      ]);
      loadStatuses();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reorder statuses');
    }
  }

  function canEdit(s: ClaimStatus) {
    return isSuperAdmin || s.scope === 'tenant';
  }

  const sortedStatuses = [...statuses].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-4">
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-green-800 text-sm">{success}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between gap-4">
        <div className="w-64">
          <Label htmlFor="job-type-select">Usecase</Label>
          <Select value={jobTypeId?.toString() ?? ''} onValueChange={v => setJobTypeId(Number(v))}>
            <SelectTrigger id="job-type-select">
              <SelectValue placeholder="Select usecase" />
            </SelectTrigger>
            <SelectContent>
              {jobTypes.map(jt => (
                <SelectItem key={jt.id} value={jt.id.toString()}>{jt.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button disabled={!jobTypeId}>
              <Plus className="h-4 w-4 mr-2" />
              Add Status
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Claim Status</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="status-label">Status Label</Label>
                <Input
                  id="status-label"
                  value={newLabel}
                  onChange={e => setNewLabel(e.target.value)}
                  placeholder="e.g. Claim Repudiated"
                />
                {newLabel.trim() && (
                  <p className="text-xs text-gray-500">Code: {slugify(newLabel)}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map(c => (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setNewColor(c.value)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${c.cls} ${newColor === c.value ? 'ring-2 ring-offset-1 ring-gray-500' : ''}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving || !newLabel.trim()}>
                {saving ? 'Adding…' : 'Add Status'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : sortedStatuses.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border rounded-lg">
          <p>No statuses configured for this usecase yet.</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Status</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Order</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedStatuses.map((s, i) => (
              <TableRow key={s.id}>
                <TableCell>
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${colorCls(s.color)}`}>
                    {s.label}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-gray-500 font-mono">{s.code}</TableCell>
                <TableCell>
                  {s.scope === 'global' ? (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                      <Lock className="h-3 w-3" /> Superadmin
                    </span>
                  ) : (
                    <span className="text-xs text-gray-600">Tenant</span>
                  )}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={s.is_active}
                    disabled={!canEdit(s)}
                    onCheckedChange={() => handleToggleActive(s)}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      variant="ghost" size="sm" disabled={!canEdit(s) || i === 0}
                      onClick={() => handleReorder(s, -1)}
                    >
                      <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" disabled={!canEdit(s) || i === sortedStatuses.length - 1}
                      onClick={() => handleReorder(s, 1)}
                    >
                      <ArrowDown className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
